import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Info, ChevronRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatCurrency, formatPercent, formatVariance } from '@/lib/format';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useScopeStore, type NodesResponse } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { currentFiscalYear, fiscalPositionOfMonth } from '@/lib/fiscal-year';
import {
  buildScopeOptions, scopeToValue, valueToScope, selectScopeCells, formatCountedMonths,
  MONTH_TARGET_INCOMPLETE_MESSAGE, type ChildScope,
} from '@/lib/kpi-target-scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/dashboard/targets` (v0.4 追加要件⑭ 増分④、D-466②)。
 * 年度累計 / 四半期累計 / 月次 の目標・実績・達成率・乖離を並べる読み取り専用画面。
 *
 * ⚠️ 累計の達成率は「経過済み (settled) の月だけ」を対象にする (to_date)。
 *    年間目標に対する実績累計で出すと4月時点で「年度達成率 8%」となり未達に見える (設計 §6-1)。
 *    期間全体の目標に対する比は「進捗率」として区別して並べる (D-940)。
 * ⚠️ 自前の目標が無い組織は配下の目標の合計を使う (D-936)。その旨を必ず画面に出す (D-943)。
 * ⚠️ rollup_gap は**警告色にしない** — 上位ノードの目標と配下合計のどちらが正とも決めていない (D-413)。
 *
 * 設計: docs/design/v04-sales-target-planning-detail.md §7-2 /
 *       docs/design/v04-sales-target-rollup-progress-rate-detail.md §5-1
 */

type ActualState = 'settled' | 'pending' | 'no_data' | 'not_published';

type MonthRow = {
  month: string; target: number | null; actual: number | null;
  achievement_rate: number | null; variance: number | null;
  source: 'manual' | 'plan_allocated' | null;
  actual_state: ActualState; actual_includes_draft: boolean;
  target_incomplete: boolean; counted: boolean;
};
type PeriodAgg = {
  target: number | null;
  target_excludes_incomplete: boolean;
  progress_rate: number | null;
  to_date: {
    months_counted: number; months: string[];
    target: number | null; actual: number | null; achievement_rate: number | null; variance: number | null;
  };
};
type TargetBasis = 'own' | 'children_sum' | 'none';
type ProgressResponse = {
  fiscal_year: number; fiscal_year_start_month: number; fiscal_year_label: string;
  node_id: string; kpi_type: string; as_of: string;
  current_position: { fiscalYear: number; quarter: number };
  target_basis: TargetBasis;
  rollup_uncovered: { nodes: { node_id: string; node_name: string }[]; total: number };
  rollup_skipped: 'too_many_nodes' | null;
  months: MonthRow[];
  quarters: (PeriodAgg & { quarter: number })[];
  year: PeriodAgg;
  plans: { plan_id: string; period_kind: 'year' | 'quarter'; quarter: number | null; target_value: number; allocation_method: string }[];
  rollup_gap: { own_target: number; children_target_sum: number; gap: number } | null;
  /** ルート→現在地のパス。⚠️ 階層の段数・種別名はテナント定義なので画面に埋め込まない */
  breadcrumb: OrgNode[];
  children: { rows: ChildRow[]; truncated: boolean; total: number };
};

type OrgNode = { node_id: string; node_name: string; node_type_code: string };
/** ⚠️ 配下一覧は年度・四半期・月次のどれでも見られるよう、期間ごとの集計を**サーバが**まとめて返す。
 *  画面側で月を足し直すと `aggregatePeriod` の二重定義になり、
 *  「確定済みの月だけで対比する」規則が片方だけ変わって食い違う (§8-55)。 */
type ChildRow = OrgNode & {
  has_children: boolean;
  target_basis: TargetBasis;
  year: PeriodAgg;
  quarters: (PeriodAgg & { quarter: number })[];
  months: MonthRow[];
};

const STATE_LABELS: Record<ActualState, string> = {
  settled: '確定',
  pending: '月未了',
  no_data: 'データ未入力',
  not_published: '未公開',
};

const monthLabel = (month: string): string => {
  const [, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${m}月`;
};

const monthLabelFull = (month: string): string => {
  const [y, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${y}年${m}月`;
};

export function KpiTargetProgressPage() {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): KpiTargetsPage.tsx と同一方針。
  // NodeSwitcherの選択を片方向シードするのみ、Breadcrumb/ChildrenTableのドリルダウンは
  // 従来通りローカルnodeIdのみを書き換える (useScopeStoreへは書き戻さない)。
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const [nodeId, setNodeId] = useState('');
  useEffect(() => {
    if (!nodeId && globalSelectedNodeId) setNodeId(globalSelectedNodeId);
  }, [globalSelectedNodeId]);
  const effectiveNodeId = nodeId || flatNodes[0]?.node.node_id || '';
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);

  // ⚠️ 年度の境界は `tenant_settings.fiscal_year_start_month` に依存する。確定前に
  //    fiscal_year を決め打ちすると10月始まりのテナントで1年ズレたまま固定される (KpiTargetsPage と同じ)。
  const { data: settings } = useQuery<{ fiscal_year_start_month?: number }>({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<{ fiscal_year_start_month?: number }>('/tenant-settings'),
  });
  const startMonth = settings?.fiscal_year_start_month ?? 4;
  const startMonthReady = settings !== undefined;
  const defaultFiscalYear = isPinned && pinnedPeriodEnd
    ? fiscalPositionOfMonth(pinnedPeriodEnd, startMonth).fiscalYear
    : currentFiscalYear(startMonth);
  const resolvedFiscalYear = fiscalYear ?? defaultFiscalYear;

  const { data, isLoading, isError } = useQuery<ProgressResponse>({
    queryKey: ['kpi-target-progress', effectiveNodeId, resolvedFiscalYear],
    queryFn: () => apiFetch<ProgressResponse>(
      `/kpi-targets/progress?fiscal_year=${resolvedFiscalYear}&node_id=${encodeURIComponent(effectiveNodeId)}`),
    enabled: !!effectiveNodeId && startMonthReady,
  });
  const yearOptions = useMemo(() => {
    const base = defaultFiscalYear;
    return [base - 2, base - 1, base, base + 1];
  }, [defaultFiscalYear]);

  const hasAnyTarget = !!data && data.target_basis !== 'none';
  const currentQuarter = data ? data.quarters.find((q) => q.quarter === data.current_position.quarter) ?? null : null;
  const currentMonthRow = data
    ? data.months.find((m) => m.month.slice(0, 7) === data.as_of.slice(0, 7)) ?? null
    : null;
  const includesDraft = (data?.months ?? []).some((m) => m.actual_includes_draft);

  if (!nodesData) {
    return <div className="p-4 flex items-center gap-2 text-text-sub"><Loader2 className="animate-spin" size={16} />読み込み中…</div>;
  }
  if (!effectiveNodeId) {
    return <div className="p-4"><EmptyState message="組織が見つかりません" description="組織階層が設定されていません。" /></div>;
  }

  return (
    <div className="p-4 space-y-4">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-text-main">目標と実績</h1>
        <p className="text-sm text-text-sub">年度・四半期の累計と月次で、売上目標に対する達成状況と乖離を確認します。</p>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1" htmlFor="ktp-node">対象組織</label>
          <Select value={effectiveNodeId} onValueChange={setNodeId}>
            <SelectTrigger id="ktp-node" className="w-full min-h-[44px]"><SelectValue placeholder="組織を選ぶ" /></SelectTrigger>
            <SelectContent>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 w-full sm:w-72">
          <label className="block text-xs text-text-sub mb-1" htmlFor="ktp-fy">年度</label>
          <Select value={String(resolvedFiscalYear)} onValueChange={(v) => setFiscalYear(Number(v))}>
            <SelectTrigger id="ktp-fy" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}年度</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isPinned && pinnedPeriodEnd && (
        <p className="text-xs text-text-sub flex items-center gap-1">
          <Info size={12} className="shrink-0" aria-hidden="true" />
          固定範囲（〜{monthLabelFull(pinnedPeriodEnd)}）に基づき{resolvedFiscalYear}年度を表示中
        </p>
      )}
      {data && <p className="text-xs text-text-sub">{data.fiscal_year_label}</p>}
      {isLoading && <div className="flex items-center gap-2 text-text-sub"><Loader2 className="animate-spin" size={16} />読み込み中…</div>}
      {isError && <p className="text-sm text-danger-text">目標の取得に失敗しました。時間をおいて再度お試しください。</p>}

      {data && !hasAnyTarget && !data.rollup_skipped && (
        <EmptyState
          message="この年度の売上目標が登録されていません"
          description="「売上目標」画面から年間・四半期・月別のいずれかで目標を登録してください。"
        />
      )}
      {data && data.rollup_skipped === 'too_many_nodes' && (
        <InfoNote>
          配下の組織が多いため、目標の合計は計算していません。下の「配下の組織」から店舗やブランドを選んでください。
        </InfoNote>
      )}

      {data && <Breadcrumb path={data.breadcrumb} onSelect={setNodeId} />}

      {data && hasAnyTarget && (
        <>
          {data.rollup_gap && data.rollup_gap.gap !== 0 && (
            /* ⚠️ D-413: どちらが正とも決めないため警告色 (赤) を使わない */
            <InfoNote>
              この組織の目標 {formatCurrency(data.rollup_gap.own_target)} に対し、
              配下組織の目標合計は {formatCurrency(data.rollup_gap.children_target_sum)} です
              （差 {formatVariance(data.rollup_gap.gap)}）。
            </InfoNote>
          )}

          {data.target_basis === 'children_sum' && (
            /* ⚠️ D-943: 会社の目標と配下の合計を取り違えないよう明示する。赤を使わない */
            <InfoNote>
              <span className="block">この組織には目標が登録されていないため、配下の組織の目標の合計を表示しています。</span>
              {data.rollup_uncovered.total > 0 && <UncoveredNote uncovered={data.rollup_uncovered} />}
            </InfoNote>
          )}

          <details className="group text-xs text-text-sub">
            <summary className="cursor-pointer min-h-[44px] inline-flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden">
              <ChevronRight size={14} className="shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
              達成率と進捗率の違い
            </summary>
            <dl className="mt-1 space-y-1 pl-2">
              <div><dt className="inline font-medium text-text-body">達成率（経過分）</dt><dd className="inline">：実績 ÷ 経過した月の目標</dd></div>
              <div><dt className="inline font-medium text-text-body">進捗率</dt><dd className="inline">：実績 ÷ 期間（年度・四半期）の目標</dd></div>
            </dl>
          </details>

          {includesDraft && (
            <p className="text-sm text-alert-text bg-alert-bg border border-alert-border rounded px-3 py-2">
              未公開のデータを含めて集計しています。公開後に数値が変わる可能性があります。
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <SummaryCard title="年度累計" agg={data.year} periodLabel="年度" />
            <SummaryCard title={`第${data.current_position.quarter}四半期 累計`} agg={currentQuarter} periodLabel="四半期" />
            <MonthCard row={currentMonthRow} />
          </div>

          <QuarterTable quarters={data.quarters} />
          <MonthTable months={data.months} />
          <ChildrenTable children={data.children} self={data} months={data.months.map((m) => m.month)}
            onDrillDown={setNodeId} />
        </>
      )}

      {/* 目標が無い階層でも下へ辿れるようにする (上位に目標を置かない運用があるため) */}
      {data && !hasAnyTarget && (
        <ChildrenTable children={data.children} self={data} months={data.months.map((m) => m.month)}
          onDrillDown={setNodeId} />
      )}
    </div>
  );
}

/** 中立色の注記 (既存の rollup_gap バナーと同じ見た目)。⚠️ 赤・警告色を使わない */
function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm text-text-body bg-surface-sunken border border-surface-border rounded px-3 py-2">
      <Info size={16} className="mt-0.5 shrink-0 text-text-sub" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

const UNCOVERED_PREVIEW = 5;

/** 目標が未登録で対比から外れた組織 (先頭5件、残りは開いて見る)。 */
function UncoveredNote({ uncovered }: { uncovered: ProgressResponse['rollup_uncovered'] }) {
  const head = uncovered.nodes.slice(0, UNCOVERED_PREVIEW);
  const rest = uncovered.nodes.slice(UNCOVERED_PREVIEW);
  const others = uncovered.total - head.length;
  return (
    <div className="mt-1">
      <p>
        目標が未登録の組織があり、その組織に売上がある月は達成率の計算に含めていません：
        {head.map((n) => n.node_name).join('、')}
        {others > 0 && ` ほか ${others} 組織`}
      </p>
      {rest.length > 0 && (
        <details>
          <summary className="cursor-pointer min-h-[44px] inline-flex items-center text-text-sub">すべて表示</summary>
          <p>{uncovered.nodes.map((n) => n.node_name).join('、')}</p>
        </details>
      )}
      <p className="mt-1 text-text-sub">休業などで売上の無い月は、目標 0 円を登録すると計算に含まれます。</p>
    </div>
  );
}

const clampPercent = (v: number): number => Math.max(0, Math.min(100, v * 100));

/** 進捗率のバー。目盛り (▲) は「ここまでの目標」= 経過分の目標 ÷ 期間の目標。
 *  ⚠️ 進捗が目盛りに届かなくても赤を使わない (数値と凡例で示す)。 */
function ProgressBar({ progress, pace, label }: { progress: number | null; pace: number | null; label: string }) {
  const legendId = useId();
  const valueNow = progress === null ? undefined : Math.round(progress * 1000) / 10;
  return (
    <div className="mt-3">
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={Math.max(100, valueNow ?? 0)}
        aria-valuenow={valueNow}
        aria-describedby={pace !== null ? legendId : undefined}
        className="relative h-2 rounded-full bg-surface-sunken"
      >
        <div className="h-full rounded-full bg-chart-2" style={{ width: `${clampPercent(progress ?? 0)}%` }} />
        {pace !== null && (
          <span aria-hidden="true" className="absolute top-full -translate-x-1/2 text-[10px] leading-none text-text-main"
            style={{ left: `${clampPercent(pace)}%` }}>▲</span>
        )}
      </div>
      <p className="mt-3 flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs text-text-sub">
        <span>進捗率 <span className="tabular-nums font-medium text-text-main">{formatPercent(progress)}</span></span>
        {pace !== null && <span id={legendId}>▲ ここまでの目標 <span className="tabular-nums">{formatPercent(pace)}</span></span>}
      </p>
    </div>
  );
}

function SummaryCard({ title, agg, periodLabel }: { title: string; agg: PeriodAgg | null; periodLabel: string }) {
  const pace = agg && agg.target !== null && agg.target > 0 && agg.to_date.target !== null
    ? agg.to_date.target / agg.target : null;
  return (
    <section className="rounded border border-surface-border bg-surface-card p-4">
      <h2 className="text-xs text-text-sub">{title}</h2>
      <p className="mt-0.5 text-xs text-text-sub">{formatCountedMonths(agg?.to_date.months ?? [])}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-text-main">
        {formatPercent(agg?.to_date.achievement_rate ?? null)}
        <span className="ml-1 text-xs font-normal text-text-sub">達成率（経過分）</span>
      </p>
      <ProgressBar progress={agg?.progress_rate ?? null} pace={pace} label={`${periodLabel}目標に対する進捗率`} />
      <dl className="mt-2 space-y-1 text-xs text-text-sub">
        <div className="flex justify-between gap-2">
          <dt>目標 (経過分)</dt><dd className="tabular-nums">{formatCurrency(agg?.to_date.target ?? null)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>実績 (経過分)</dt><dd className="tabular-nums">{formatCurrency(agg?.to_date.actual ?? null)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>乖離</dt><dd className="tabular-nums">{formatVariance(agg?.to_date.variance ?? null)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>{agg?.target_excludes_incomplete ? `${periodLabel}の目標（一部未登録の月を除く）` : `${periodLabel}の目標`}</dt>
          <dd className="tabular-nums">{formatCurrency(agg?.target ?? null)}</dd>
        </div>
      </dl>
    </section>
  );
}

function MonthCard({ row }: { row: MonthRow | null }) {
  return (
    <section className="rounded border border-surface-border bg-surface-card p-4">
      <h2 className="text-xs text-text-sub">当月</h2>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-text-main">
        {formatPercent(row?.achievement_rate ?? null)}
        <span className="ml-1 text-xs font-normal text-text-sub">達成率</span>
      </p>
      {row?.target_incomplete && <p className="mt-1 text-xs text-text-sub">{MONTH_TARGET_INCOMPLETE_MESSAGE}</p>}
      <dl className="mt-2 space-y-1 text-xs text-text-sub">
        <div className="flex justify-between gap-2"><dt>目標</dt><dd className={`tabular-nums ${row?.target_incomplete ? 'text-text-muted' : ''}`}>{formatCurrency(row?.target ?? null)}</dd></div>
        <div className="flex justify-between gap-2"><dt>実績</dt><dd className="tabular-nums">{formatCurrency(row?.actual ?? null)}</dd></div>
        <div className="flex justify-between gap-2"><dt>乖離</dt><dd className="tabular-nums">{formatVariance(row?.variance ?? null)}</dd></div>
      </dl>
      <p className="mt-2 text-xs text-text-sub">{row ? STATE_LABELS[row.actual_state] : '−'}</p>
    </section>
  );
}

/** 横スクロールする表の固定列。⚠️ 背景は不透明色にする (半透明だとスクロール時に下のセルが透ける) */
const STICKY_COL = 'sticky left-0 z-10 bg-surface-card border-r border-surface-line';

function QuarterTable({ quarters }: { quarters: (PeriodAgg & { quarter: number })[] }) {
  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-2">
      <h2 className="text-sm font-semibold text-text-main">四半期</h2>
      <div className="overflow-x-auto">
        <table className="relative min-w-full text-sm">
          <caption className="sr-only">四半期ごとの目標と実績</caption>
          <thead>
            <tr className="text-left text-text-sub border-b border-surface-border">
              <th scope="col" className={`${STICKY_COL} py-2 pr-4 font-medium whitespace-nowrap`}>四半期</th>
              <th scope="col" className="relative py-2 px-4 font-medium whitespace-nowrap text-right">期間目標</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">目標 (経過分)</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">実績 (経過分)</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">達成率（経過分）</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">進捗率</th>
              <th scope="col" className="relative py-2 font-medium whitespace-nowrap text-right">乖離</th>
            </tr>
          </thead>
          <tbody>
            {quarters.map((q) => (
              <tr key={q.quarter} className="border-b border-surface-line">
                <td className={`${STICKY_COL} py-2 pr-4 whitespace-nowrap`}>第{q.quarter}四半期</td>
                <td className="py-2 px-4 text-right tabular-nums whitespace-nowrap">
                  {formatCurrency(q.target)}
                  {q.target_excludes_incomplete && <span className="block text-xs text-text-sub">一部未登録の月を除く</span>}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(q.to_date.target)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(q.to_date.actual)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(q.to_date.achievement_rate)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(q.progress_rate)}</td>
                <td className="py-2 text-right tabular-nums whitespace-nowrap">{formatVariance(q.to_date.variance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MonthTable({ months }: { months: MonthRow[] }) {
  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-2">
      <h2 className="text-sm font-semibold text-text-main">月次</h2>
      <div className="overflow-x-auto">
        <table className="relative min-w-full text-sm">
          <caption className="sr-only">月ごとの目標と実績</caption>
          <thead>
            <tr className="text-left text-text-sub border-b border-surface-border">
              <th scope="col" className={`${STICKY_COL} py-2 pr-4 font-medium whitespace-nowrap`}>月</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">目標</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">実績</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">達成率</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">乖離</th>
              <th scope="col" className="relative py-2 font-medium whitespace-nowrap">状態</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className="border-b border-surface-line">
                <td className={`${STICKY_COL} py-2 pr-4 whitespace-nowrap`}>{monthLabel(m.month)}</td>
                <td className={`py-2 pr-4 text-right tabular-nums whitespace-nowrap ${m.target_incomplete ? 'text-text-sub' : ''}`}>{formatCurrency(m.target)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(m.actual)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(m.achievement_rate)}</td>
                <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatVariance(m.variance)}</td>
                <td className="py-2 whitespace-nowrap">
                  <span className="text-xs text-text-sub">{STATE_LABELS[m.actual_state]}</span>
                  {m.actual_includes_draft && <span className="ml-1 text-xs text-alert-text">（未公開含む）</span>}
                  {m.target_incomplete && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-text-sub">
                      <Info size={12} aria-hidden="true" />一部未登録のため計算に含めていません
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** ルート→現在地のパンくず。各段をクリックするとその階層へ戻る。
 *  ⚠️ 「全社 / ブランド / エリア / 店舗」といった段数・呼称はテナントが自由に定義する
 *  (§6-0 原則1、node_type_definitions) ため、**画面に固定の階層名を書かない**。
 *  返ってきたパスをそのまま並べるだけにする。 */
function Breadcrumb({ path, onSelect }: { path: OrgNode[]; onSelect: (id: string) => void }) {
  if (path.length === 0) return null;
  return (
    <nav aria-label="組織階層" className="flex flex-wrap items-center gap-1 text-sm">
      {path.map((node, i) => {
        const isCurrent = i === path.length - 1;
        return (
          <span key={node.node_id} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={14} className="text-text-muted shrink-0" aria-hidden="true" />}
            {isCurrent ? (
              <span aria-current="page" className="min-h-[44px] inline-flex items-center px-2 font-semibold text-text-main">
                {node.node_name}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onSelect(node.node_id)}
                className="min-h-[44px] inline-flex items-center px-2 rounded text-text-sub hover:bg-surface-sunken hover:text-text-main"
              >
                {node.node_name}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/** 直下の組織の達成状況。行をクリックするとその組織へドリルダウンする。
 *  ⚠️ 集計単位 (年度 / 四半期 / 月) を切り替えられる。切り替えても再取得は不要
 *  (サーバが3粒度すべてを返しているため)。 */
function ChildrenTable({ children, self, months, onDrillDown }: {
  children: ProgressResponse['children'];
  /** 選択中の組織自身 (集計単位ごとの計算対象月を表示するため) */
  self: Pick<ProgressResponse, 'year' | 'quarters' | 'months'>;
  /** ⚠️ サーバが返す年度順のまま渡す (年度開始月が4月とは限らないため並べ替えない) */
  months: string[];
  onDrillDown: (id: string) => void;
}) {
  const [scope, setScope] = useState<ChildScope>({ kind: 'year' });

  if (children.truncated) {
    return (
      <section className="rounded border border-surface-border bg-surface-card p-4">
        <h2 className="text-sm font-semibold text-text-main">配下の組織</h2>
        <p className="mt-2 text-sm text-text-sub">
          配下の組織が {children.total} 件あるため一覧を表示していません。上の「対象組織」から直接選んでください。
        </p>
      </section>
    );
  }
  if (children.rows.length === 0) return null;

  const scopeOptions = buildScopeOptions(months, monthLabel);
  const totalLabel = scope.kind === 'year' ? '年度目標' : scope.kind === 'quarter' ? '四半期目標' : '月次目標';
  const showToDate = scope.kind !== 'month';
  const selfMonths = selectScopeCells(scope, self).months;
  const sameMonths = (a: string[]): boolean => a.length === selfMonths.length && a.every((m, i) => m === selfMonths[i]);
  const anyDiffers = showToDate && children.rows.some((r) => !sameMonths(selectScopeCells(scope, r).months));

  return (
    <section className="rounded border border-surface-border bg-surface-card p-4 space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-main">配下の組織</h2>
          <p className="text-xs text-text-sub">組織名を選ぶとその組織の達成状況に切り替わります。</p>
        </div>
        <div className="min-w-0 w-full sm:w-56">
          <label className="block text-xs text-text-sub mb-1" htmlFor="ktp-child-scope">集計単位</label>
          <Select value={scopeToValue(scope)} onValueChange={(v) => setScope(valueToScope(v))}>
            <SelectTrigger id="ktp-child-scope" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {scopeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* ⚠️ live 領域は常に置き、中身だけを入れ替える (差し込まれた live 領域は読み上げられない) */}
          <p className="mt-1 text-xs text-text-sub" aria-live="polite">{showToDate ? formatCountedMonths(selfMonths) : ''}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="relative min-w-full text-sm">
          <caption className="sr-only">配下の組織ごとの目標と実績</caption>
          <thead>
            <tr className="text-left text-text-sub border-b border-surface-border">
              <th scope="col" className={`${STICKY_COL} py-2 pr-4 font-medium whitespace-nowrap`}>組織</th>
              <th scope="col" className="relative py-2 px-4 font-medium whitespace-nowrap text-right">{totalLabel}</th>
              {showToDate && (
                <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">目標 (経過分)</th>
              )}
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">{showToDate ? '実績（経過分）' : '実績'}</th>
              <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">{showToDate ? '達成率（経過分）' : '達成率'}</th>
              {showToDate && (
                <th scope="col" className="relative py-2 pr-4 font-medium whitespace-nowrap text-right">進捗率</th>
              )}
              <th scope="col" className="relative py-2 font-medium whitespace-nowrap text-right">乖離</th>
            </tr>
          </thead>
          <tbody>
            {children.rows.map((row) => {
              const cell = selectScopeCells(scope, row);
              const differs = showToDate && !sameMonths(cell.months);
              return (
                <tr key={row.node_id} className="border-b border-surface-line">
                  <td className={`${STICKY_COL} py-1 pr-4 whitespace-nowrap`}>
                    <button
                      type="button"
                      onClick={() => onDrillDown(row.node_id)}
                      className="min-h-[44px] inline-flex items-center gap-1 px-2 -mx-2 rounded text-text-main hover:bg-surface-sunken"
                    >
                      <span className="truncate max-w-[7rem] sm:max-w-[14rem]" title={row.node_name}>{row.node_name}</span>
                      {differs && (
                        <>
                          <span className="text-xs text-text-sub" aria-hidden="true">※</span>
                          <span className="sr-only">（計算対象の月が異なります）</span>
                        </>
                      )}
                      {row.has_children && <ChevronRight size={14} className="shrink-0 text-text-sub" aria-hidden="true" />}
                    </button>
                  </td>
                  <td className="py-2 px-4 text-right tabular-nums whitespace-nowrap">
                    {formatCurrency(cell.total)}
                    {row.target_basis === 'children_sum' && <span className="block text-xs text-text-sub">配下合計</span>}
                  </td>
                  {showToDate && (
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(cell.target)}</td>
                  )}
                  <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatCurrency(cell.actual)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(cell.achievement_rate)}</td>
                  {showToDate && (
                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap">{formatPercent(cell.progress_rate)}</td>
                  )}
                  <td className="py-2 text-right tabular-nums whitespace-nowrap">{formatVariance(cell.variance)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {anyDiffers && (
        <p className="text-xs text-text-sub">※ 目標が一部未登録の月などがあるため、計算に使った月が上の表示と異なります。</p>
      )}
    </section>
  );
}
